import React, { useState, useEffect } from 'react';
import Layout from "../Shared/Layout";
import useAuthService from '../service/api/useAuthService';
import { updateAccountCheck } from '../service/api/accountService';
import { toast } from 'sonner';
import md5 from 'crypto-js/md5';

const EditProfile = () => {
    const { userData, loading, error } = useAuthService();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        dob: '',
        phoneNumber: '',
        email: '',
        job: '',
        picture: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImage, setPreviewImage] = useState('/assets/images/user.jpg');

    useEffect(() => {
        if (userData && !loading) {
            const accountId = parseInt(userData.accountId, 10);
            setFormData({
                accountId : accountId,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                dob: userData.dob ? userData.dob.split('T')[0] : '',
                phoneNumber: userData.phoneNumber || '',
                email: userData.email || '',
                job: userData.job || '',
                picture: userData.picture, // Valeur par défaut
            });

            setPreviewImage(getImageUrl(userData.picture, userData.email));
        }
    }, [userData, loading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedImage(file);
            setPreviewImage(URL.createObjectURL(file)); // Pour la prévisualisation immédiate
            setFormData((prevData) => ({
                ...prevData,
                picture: file, // Stocke le fichier directement
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormErrors({});
    
        // Validation du formulaire
        const errors = {};
        if (!formData.firstName.trim()) errors.firstName = 'Le prénom est obligatoire';
        if (!formData.lastName.trim()) errors.lastName = 'Le nom est obligatoire';
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        
        // Construction de FormData pour envoyer le fichier et les autres champs
        const formPayload = new FormData();
        formPayload.append("firstName", formData.firstName);
        formPayload.append("lastName", formData.lastName);
        formPayload.append("dob", formData.dob);
        formPayload.append("phoneNumber", formData.phoneNumber);
        formPayload.append("email", formData.email);
        formPayload.append("job", formData.job);
        // Ajoute le fichier seulement s'il a été sélectionné
        if (selectedImage) {
            formPayload.append("picture", selectedImage);
        } else if (typeof formData.picture === "string") {
            // Si aucune image n'est sélectionnée et que tu as déjà un chemin en base,
            // tu peux éventuellement le renvoyer. Attention : le backend attend un IFormFile.
            // Dans ce cas, il faudra gérer la logique côté backend pour ne pas écraser l'image existante.
            formPayload.append("picture", formData.picture);
        }
    
        // Pour déboguer, affiche le contenu du FormData
        for (let pair of formPayload.entries()) {
            console.log(pair[0] + ": " + pair[1]);
        }
    
        try {
            console.log("Données envoyées :", formPayload);
            await updateAccountCheck(formPayload);
            toast.success('Profil mis à jour avec succès !');
        } catch (error) {
            toast.error(error.message || 'Une erreur est survenue lors de la mise à jour du profil.');
        }
    };
    

    // Gérer le gravatar
    const getGravatarUrl = (email) => {
        const gravatarHash = md5(email.trim().toLowerCase());
        return `https://www.gravatar.com/avatar/${gravatarHash}?d=mp&r=g&s=200`;
    };

    const getImageUrl = (imagePath, email) => {
        if (imagePath) {
            return `${import.meta.env.VITE_API_BASE_URL}${imagePath}`;
        } else {
            return getGravatarUrl(email);
        }
    };

    if (loading) {
        return <div>Chargement...</div>;
    }
    if (error) {
        return <div>Erreur : {error.message}</div>;
    }

    return (
        <Layout>
            <div className="content-body default-height">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="page-titles">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item text-primary">Modifier le profil</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>

                        <div className="col-xl-3 col-lg-4">
                            <div className="clearfix">
                                <div className="card card-bx profile-card author-profile m-b30">
                                    <div className="card-body">
                                        <div className="p-5">
                                            <div className="author-profile">
                                                <div className="author-media">
                                                    <img src={previewImage} alt="Profile" />
                                                    <div className="upload-link" title="" data-toggle="tooltip" data-placement="right" data-original-title="update">
                                                        <input type="file" className="update-flie" onChange={handleImageChange} />
                                                        <i className="fa fa-camera"></i>
                                                    </div>
                                                </div>
                                                <div className="author-info">
                                                    <h6 className="title">{userData?.firstName} {userData?.lastName}</h6>
                                                    <span className="text-primary">{userData?.job}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="info-list">
                                            <ul>
                                                <li><a href="#">E-mail</a><span>{userData?.email}</span></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-9 col-lg-8">
                            <div className="card profile-card card-bx m-b30">
                                <div className="card-header">
                                    <h6 className="card-title">Configuration du compte</h6>
                                </div>

                                <form className="profile-form" onSubmit={handleSubmit}>
                                     {/* Champ caché pour accountId */}
                                    <input
                                        type="hidden"
                                        name="accountId"
                                        value={formData.accountId}
                                    />
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-sm-6 m-b30">
                                                <label className="form-label">Nom</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                />
                                                {formErrors.firstName && <div className="invalid-feedback">{formErrors.firstName}</div>}
                                            </div>
                                            <div className="col-sm-6 m-b30">
                                                <label className="form-label">Prenom</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`}
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                />
                                                {formErrors.lastName && <div className="invalid-feedback">{formErrors.lastName}</div>}
                                            </div>

                                            <div className="col-sm-6 m-b30">
                                                <label className="form-label">Date de naissance</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="dob"
                                                    value={formData.dob}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="col-sm-6 m-b30">
                                                <label className="form-label">Téléphone</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="phoneNumber"
                                                    value={formData.phoneNumber}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="col-sm-6 m-b30">
                                                <label className="form-label">Adresse email</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="email"
                                                    value={formData.email}
                                                    disabled
                                                />
                                            </div>
                                            <div className="col-sm-6 m-b30">
                                                <label className="form-label">Travail</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="job"
                                                    value={formData.job}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer">
                                        <button type="submit" className="btn btn-primary">Enregistrer les modifications</button>

                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default EditProfile;
